import { mmToCssPx } from "./units";

/** Canonical reader presentation of the continuous digital book.
 * - responsive: the native viewport is the reader's device width (default, and
 *   what an absent `digital` field means; never written implicitly).
 * - fixed-page: the native viewport is one common width, the widest page trim,
 *   scaled from outside to the reader's device width.
 * Page geometry (trim/bleed/margins) stays print-owned; this never touches it. */
export const DIGITAL_PRESENTATIONS = ["responsive", "fixed-page"] as const;
export type DigitalPresentation = (typeof DIGITAL_PRESENTATIONS)[number];
export type DigitalViewportPolicy = "device-width" | "widest-page";

/** Concise reader-facing statement of the mixed-trim limit. */
export const DIGITAL_FIXED_PAGE_NOTE =
  "Fixed page: every page is shown at its physical size, and the digital viewport is the widest page.";

/** Supported ranges, in CSS px. Trim bounds mirror the document schema (40-1000 mm). */
export const DIGITAL_DEVICE_WIDTH_RANGE = { min: 200, max: 4096 } as const;
export const DIGITAL_PREVIEW_HEIGHT_RANGE = { min: 100, max: 4000 } as const;
export const DIGITAL_TRIM_WIDTH_MM_RANGE = { min: 40, max: 1000 } as const;

export function isDigitalPresentation(
  value: unknown,
): value is DigitalPresentation {
  return (DIGITAL_PRESENTATIONS as readonly unknown[]).includes(value);
}

/** Absent policy is the current responsive behavior. */
export function resolveDigitalPresentation(doc: {
  digital?: { presentation: DigitalPresentation };
}): DigitalPresentation {
  return doc.digital?.presentation ?? "responsive";
}

function assertInRange(
  value: number,
  range: { min: number; max: number },
  what: string,
  unit: string,
) {
  if (!Number.isFinite(value) || value < range.min || value > range.max) {
    throw new Error(
      `${what} must be a finite number from ${range.min} to ${range.max} ${unit}`,
    );
  }
}

/** The widest physical trim, converted at 96 CSS px per inch. Mixed trims all
 * share this one native viewport (a documented limit: a narrower page's own
 * screen variants see the widest page's width). An empty book is refused. */
export function resolveFixedPageViewportWidth(
  pages: readonly { geometry: { trim: { width: number } } }[],
): number {
  if (!pages.length) {
    throw new Error("Fixed-page presentation needs at least one page");
  }
  let widest = 0;
  for (const page of pages) {
    const mm = page.geometry.trim.width;
    assertInRange(mm, DIGITAL_TRIM_WIDTH_MM_RANGE, "Page trim width", "mm");
    widest = Math.max(widest, mm);
  }
  return mmToCssPx(widest);
}

export type DigitalReaderPresentation = {
  presentation: DigitalPresentation;
  viewportPolicy: DigitalViewportPolicy;
  /** Width the native iframe (and therefore its media queries) is laid out at. */
  viewportWidth: number;
  /** External scale applied to the iframe element only. */
  scale: number;
  /** Native iframe height, so the scaled frame fills the preview height. */
  nativeHeight: number;
  /** Outer wrapper box: native size times scale. */
  wrapperWidth: number;
  wrapperHeight: number;
};

export function calculateDigitalReaderPresentation(input: {
  presentation: DigitalPresentation;
  deviceWidth: number;
  previewHeight: number;
  pages: readonly { geometry: { trim: { width: number } } }[];
}): DigitalReaderPresentation {
  const { presentation, deviceWidth, previewHeight, pages } = input;
  if (!isDigitalPresentation(presentation)) {
    throw new Error("Unknown digital presentation");
  }
  assertInRange(
    deviceWidth,
    DIGITAL_DEVICE_WIDTH_RANGE,
    "Device width",
    "px",
  );
  assertInRange(
    previewHeight,
    DIGITAL_PREVIEW_HEIGHT_RANGE,
    "Preview height",
    "px",
  );
  if (presentation === "responsive") {
    return {
      presentation,
      viewportPolicy: "device-width",
      viewportWidth: deviceWidth,
      scale: 1,
      nativeHeight: previewHeight,
      wrapperWidth: deviceWidth,
      wrapperHeight: previewHeight,
    };
  }
  const viewportWidth = resolveFixedPageViewportWidth(pages);
  const scale = deviceWidth / viewportWidth;
  return {
    presentation,
    viewportPolicy: "widest-page",
    viewportWidth,
    scale,
    nativeHeight: previewHeight / scale,
    wrapperWidth: viewportWidth * scale,
    wrapperHeight: previewHeight,
  };
}
